import React, { useState } from 'react';
import './ContinueRaceModal.css';

interface Props {
  currentRaceName: string;
  newRaceName: string;
  /** True when the new race is after the current one — continuation only makes
   *  sense forward in time. When false, only "start fresh" is offered. */
  canContinue: boolean;
  /** Roll the current race into the new one (keeps fitness, recovery week). */
  onContinue: () => Promise<void>;
  /** Replace the plan with a brand-new training cycle. */
  onFresh: () => Promise<void>;
  onClose: () => void;
}

const ContinueRaceModal: React.FC<Props> = ({
  currentRaceName, newRaceName, canContinue, onContinue, onFresh, onClose,
}) => {
  const [busy, setBusy] = useState<null | 'continue' | 'fresh'>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (which: 'continue' | 'fresh', fn: () => Promise<void>) => {
    setBusy(which);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.message || 'Something went wrong. Please try again.');
      setBusy(null);
    }
  };

  return (
    <div className="continue-overlay" onClick={busy ? undefined : onClose}>
      <div className="continue-modal" onClick={e => e.stopPropagation()}>
        <h3>You're already training for {currentRaceName}</h3>
        <p className="continue-sub">
          How do you want to handle <strong>{newRaceName}</strong>?
        </p>

        {error && <div className="continue-error">{error}</div>}

        {canContinue && (
          <button
            className="continue-option continue-option--primary"
            onClick={() => run('continue', onContinue)}
            disabled={!!busy}
          >
            <div className="continue-option-title">
              Continue into it <span className="continue-pill">Recommended</span>
            </div>
            <div className="continue-option-desc">
              Keep your current fitness. We'll treat {currentRaceName} as a tune-up,
              give you a recovery week after, and build straight to {newRaceName} —
              no restarting from base.
            </div>
            {busy === 'continue' && <div className="continue-spinner">Setting up…</div>}
          </button>
        )}

        <button
          className="continue-option"
          onClick={() => run('fresh', onFresh)}
          disabled={!!busy}
        >
          <div className="continue-option-title">Start a fresh plan</div>
          <div className="continue-option-desc">
            Replace your current plan with a brand-new training cycle for {newRaceName}.
          </div>
          {busy === 'fresh' && <div className="continue-spinner">Generating…</div>}
        </button>

        <button className="continue-cancel" onClick={onClose} disabled={!!busy}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ContinueRaceModal;
